import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { Paths, SEARCH_RESULTS_PROXY_PATHS } from '../constants/paths';
import { useGates } from '../hooks/useGates';
import { jsx as _jsx } from "react/jsx-runtime";
const LocationHistoryContext = /*#__PURE__*/createContext({
  getBackLinkLocation: () => undefined
});

/**
 * Checks if a pathname is a search results proxy path.
 * Proxy paths (like /apps/all) redirect to /explore with filters and should be skipped.
 */
function isSearchResultsProxyPath(location, solutionsSpringSpotlightUngated, tabularSearchUngated) {
  if (solutionsSpringSpotlightUngated && location.pathname === Paths.SEARCH_RESULTS && (!location.search.includes('eco_search=') || !tabularSearchUngated)) {
    return true;
  }
  return SEARCH_RESULTS_PROXY_PATHS.some(path => matchPath(location.pathname, {
    path,
    exact: true
  }));
}

/**
 * Checks if a pathname is a listing detail page.
 * Listing pages should never be used as back link destinations.
 */
function isListingDetailPath(pathname) {
  return !!matchPath(pathname, {
    path: Paths.LISTING_DETAILS,
    exact: true
  });
}
export function LocationHistoryProvider({
  children
}) {
  const location = useLocation();
  const gates = useGates();

  // Initialize history, skipping proxy paths
  const [_history, setHistory] = useState(() => {
    if (isSearchResultsProxyPath(location, gates.solutionsSpringSpotlight, gates.tabularSearch)) {
      return [];
    }
    return [location];
  });
  useEffect(() => {
    setHistory(prevHistory => {
      // Skip proxy paths entirely - they redirect and shouldn't be in history
      if (isSearchResultsProxyPath(location, gates.solutionsSpringSpotlight, gates.tabularSearch)) {
        return prevHistory;
      }
      if (prevHistory.length === 0) {
        return [location];
      }
      const currentLocation = prevHistory[prevHistory.length - 1];

      // Skip if location hasn't actually changed (avoids duplicate entries)
      if (currentLocation.pathname === location.pathname && currentLocation.search === location.search) {
        return prevHistory;
      }

      // Detect back navigation: when the new location matches the second-to-last entry,
      // the user pressed the browser back button. Remove the last entry to maintain accuracy.
      if (prevHistory.length >= 2) {
        const secondToLast = prevHistory[prevHistory.length - 2];
        if (secondToLast.pathname === location.pathname && secondToLast.search === location.search) {
          return prevHistory.slice(0, -1);
        }
      }

      // Forward navigation: add new location to history
      return [...prevHistory, location];
    });
  }, [location, gates.solutionsSpringSpotlight, gates.tabularSearch]);

  // Returns the last location suitable for back links
  // Skips listing detail pages and locations with the same pathname
  // Useful for "Back to [Previous Page]" links that skip filter/search refinements
  const getBackLinkLocation = useCallback(() => {
    if (_history.length < 2) {
      return undefined;
    }
    const currentPathname = _history[_history.length - 1].pathname;

    // Walk backward through history to find first suitable location:
    // - Different pathname from current page
    // - Not a listing detail page (listings should never be back destinations)
    for (let i = _history.length - 2; i >= 0; i--) {
      const historyLocation = _history[i];
      if (historyLocation.pathname !== currentPathname && !isListingDetailPath(historyLocation.pathname)) {
        return historyLocation;
      }
    }
    return undefined;
  }, [_history]);
  return /*#__PURE__*/_jsx(LocationHistoryContext.Provider, {
    value: {
      getBackLinkLocation
    },
    children: children
  });
}
export function useLocationHistory() {
  return useContext(LocationHistoryContext);
}